// Website order flow in the hub (CEO 2026-10-08): whose account an order or
// request belongs to, whose it is, and — for the manager — the Assign rep
// control. Renders nothing while WEBSITE_ORDER_FLOW is off.
import { useCallback, useEffect, useState } from "react";
import { Loader2, UserCheck } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { LocationPicker, type CompanyLocations, type LocationChoice } from "@/components/website/LocationPicker";

export type FlowKind = "order" | "request";
interface FlowSettings { on: boolean; canAssign: boolean; managerConfigured?: boolean; userId?: string }
interface Account {
  client_id: string | null; client_name: string | null; client_rep_id: string | null; client_rep_name: string | null;
  portal_user_id: string | null; portal_password_set: boolean | null; account_match: "exact" | "domain" | "new" | "picked" | null;
  account_linked_at: string | null; routing_status: "assigned" | "needs_rep" | "needs_location" | null;
  /** Someone new at a company already in Flow (Bozzuto): pick the location. */
  location_choices?: CompanyLocations[];
  assigned_rep_id: string | null; assigned_rep_name: string | null; assigned_at: string | null; assigned_by_name: string | null;
  customer_notified_at: string | null; staff_notified_at: string | null;
  converted_order_id: string | null; converted_order_number: number | null; converted_order_stage: string | null;
}
interface TimelineRow { at: string; event: string; actor: string | null; payload: Record<string, unknown> }
interface Rep { id: string; name: string; email: string }

const focusRing = "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink";
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const MATCH: Record<string, string> = { exact: "same email", domain: "same company email domain", new: "new account", picked: "location picked by staff" };
const EVENT: Record<string, string> = {
  "website.account_linked": "Saved to the account",
  "website.portal_login_created": "Customer login created",
  "website.portal_login_skipped": "No customer login (staff email)",
  "website.set_password_issued": "Set-password link issued",
  "website.logos_saved": "Logo saved to brand assets",
  "website.logos_not_saved": "Logo not saved",
  "website.routed_to_rep": "Went straight to the account's rep",
  "website.needs_rep": "Waiting for a rep",
  "website.needs_location": "Waiting for the location to be picked",
  "website.linked_unconfirmed": "Filed under the account — NOT yet confirmed as the client (logo kept on the request)",
  "website.request_confirmed": "Confirmed and linked to the account",
  "website.new_account_cap": "Not linked — today's new-account limit was reached",
  "website.rep_assigned": "Rep assigned",
  "website.email_sent": "Email sent",
  "website.email_failed": "Email FAILED",
  "website.sales_order_created": "Quote created for the rep",
  "website.sales_order_failed": "Quote NOT created — use Create order",
  "website.estimate_created": "Draft estimate created for the rep",
  "website.estimate_failed": "Estimate NOT created",
};

let settingsCache: Promise<FlowSettings> | null = null;
export function useFlowSettings(): FlowSettings | null {
  const [s, setS] = useState<FlowSettings | null>(null);
  useEffect(() => {
    settingsCache ??= api<FlowSettings>("/website-order-flow/settings").catch(() => ({ on: false, canAssign: false }));
    let live = true;
    void settingsCache.then((v) => { if (live) setS(v); });
    return () => { live = false; };
  }, []);
  return s;
}

/** Account + routing + timeline, shown inside an order or request panel. */
export function WebsiteAccountPanel({ kind, id, onChanged }: { kind: FlowKind; id: string; onChanged?: () => void }) {
  const settings = useFlowSettings();
  const [data, setData] = useState<{ account: Account; timeline: TimelineRow[] } | null>(null);
  const [reps, setReps] = useState<Rep[]>([]);
  const [repId, setRepId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setData(await api(`/website-order-flow/${kind}/${id}`)); } catch (e) { setError(apiErrorMessage(e, "Couldn't load the account.")); }
  }, [kind, id]);
  useEffect(() => { if (settings?.on) void load(); }, [settings?.on, load]);
  useEffect(() => {
    if (settings?.on && settings.canAssign) api<{ reps: Rep[] }>("/website-order-flow/reps").then((r) => setReps(r.reps)).catch(() => setReps([]));
  }, [settings?.on, settings?.canAssign]);

  if (!settings?.on) return null;
  const a = data?.account;
  // An existing account's email typed on the website: filed under the account
  // and routed to its rep, but not confirmed as the client until the rep says
  // so (security audit 2026-10-21, H4).
  const unconfirmed = kind === "request" && !!a?.client_id &&
    !!data?.timeline.some((t) => t.event === "website.linked_unconfirmed") &&
    !data?.timeline.some((t) => t.event === "website.request_confirmed");
  const mayConfirm = !!settings && (settings.canAssign || (!!settings.userId && a?.assigned_rep_id === settings.userId));

  const act = async (path: string, body?: unknown) => {
    setBusy(true); setError(null);
    try {
      await api(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body ?? {}) });
      await load();
      onChanged?.();
    } catch (e) { setError(apiErrorMessage(e, "That didn't work — try again.")); } finally { setBusy(false); }
  };

  return (
    <section className="flex flex-col gap-3 border-t border-rule pt-4" aria-label="Account and rep">
      <h3 className="text-[15px] font-bold">Account and rep</h3>
      {!data && !error ? <p className="flex items-center gap-2 text-muted"><Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading…</p> : null}
      {error ? <p role="alert" className="text-risk">{error}</p> : null}
      {a && !a.client_id && a.routing_status === "needs_location" && (a.location_choices?.length ?? 0) > 0 ? (
        <LocationPicker
          companies={a.location_choices ?? []}
          pickedId={null}
          disabled={busy}
          onPick={async (choice: LocationChoice) => {
            try {
              await api(`/website-order-flow/${kind}/${id}/link`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(choice) });
            } catch (e) {
              throw new Error(apiErrorMessage(e, "Couldn't save the location."));
            }
            await load();
            onChanged?.();
          }}
        />
      ) : a && !a.client_id ? (
        <div className="flex flex-col gap-2">
          <p className="text-muted">Not saved to an account yet{kind === "order" ? " (paid before the account step was switched on)" : ""}.</p>
          {settings.canAssign ? <div><Button variant="outline" disabled={busy} onClick={() => void act(`/website-order-flow/${kind}/${id}/link`)}>Link account</Button></div> : null}
        </div>
      ) : null}
      {unconfirmed ? (
        <div role="note" className="flex flex-col gap-2 rounded-lg border border-warn bg-warn-bg p-3">
          <p className="font-semibold text-warn">From the website — not yet confirmed as the client</p>
          <p className="text-[13px]">Anyone can type any email on the website. Check it's really them (reply from your own email or call) before sharing anything about the account. Their logo is kept on this request only, and they got just a short "we've received it" note.</p>
          {mayConfirm ? (
            <div><Button variant="outline" disabled={busy} onClick={() => void act(`/website-order-flow/request/${id}/confirm`)}>Link to account</Button></div>
          ) : null}
        </div>
      ) : null}
      {a && a.client_id ? (
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
          <dt className="text-muted">Account</dt>
          <dd><span className="font-semibold">{a.client_name}</span>{a.account_match ? <span className="text-muted"> · {MATCH[a.account_match]}</span> : null}</dd>
          <dt className="text-muted">Customer login</dt>
          <dd>{a.portal_user_id ? (a.portal_password_set ? "Active" : "Waiting for the customer to set a password") : "None"}</dd>
          <dt className="text-muted">Rep</dt>
          <dd>
            {a.routing_status === "assigned" && a.assigned_rep_name
              ? <span className="inline-flex items-center gap-1 font-semibold"><UserCheck className="h-4 w-4" aria-hidden /> {a.assigned_rep_name}</span>
              : <span className="inline-block rounded bg-warn-bg px-1.5 font-semibold text-warn">Needs a rep</span>}
            {a.assigned_by_name ? <span className="text-muted"> · assigned by {a.assigned_by_name} {when(a.assigned_at)}</span> : null}
          </dd>
          <dt className="text-muted">{kind === "order" ? "Quote" : "Estimate"}</dt>
          <dd>{a.converted_order_number
            ? <span className="font-semibold">#{a.converted_order_number}{kind === "order" ? " · payment recorded · rep reviews, then invoices" : " · draft for the rep to price"}</span>
            : <span className="text-muted">{a.routing_status === "assigned" ? "Not created yet" : "Created when a rep is assigned"}</span>}</dd>
        </dl>
      ) : null}
      {a && a.client_id && settings.canAssign ? (
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-[13px] font-semibold">
            {a.routing_status === "assigned" ? "Change rep" : "Assign rep"}
            <select value={repId} onChange={(e) => setRepId(e.target.value)} className={cn("h-10 rounded-lg border border-rule bg-white px-3 text-[16px] font-normal", focusRing)}>
              <option value="">Choose a rep…</option>
              {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </label>
          <Button disabled={busy || !repId} onClick={() => void act(`/website-order-flow/${kind}/${id}/assign`, { repId })}>Assign rep</Button>
          <p className="w-full text-[13px] text-muted">Makes them {a.client_name}'s rep too, so this account's next orders go straight to them. They get an email.</p>
        </div>
      ) : null}
      {data && data.timeline.length ? (
        <details>
          <summary className={cn("cursor-pointer rounded text-[13px] font-semibold", focusRing)}>History ({data.timeline.length})</summary>
          <ol className="mt-2 flex flex-col gap-1 text-[13px]">
            {data.timeline.map((t, i) => (
              <li key={i} className="flex gap-3">
                <span className="w-[120px] shrink-0 text-muted tabular-nums">{when(t.at)}</span>
                <span>
                  {EVENT[t.event] ?? t.event}
                  {t.event.startsWith("website.email") && typeof t.payload.templateId === "string" ? ` — ${String(t.payload.templateId).replace("website.", "").replace(/_/g, " ")} to ${(t.payload.to as string[] | undefined)?.join(", ") ?? ""}` : ""}
                  {t.actor ? <span className="text-muted"> · {t.actor}</span> : null}
                </span>
              </li>
            ))}
          </ol>
        </details>
      ) : null}
    </section>
  );
}

interface QueueRow {
  kind: FlowKind; id: string; status: string; contact: { name: string; email: string; company?: string | null };
  total: string | null; at: string; client_name: string | null; routing_status: string | null; rep_name: string | null; account_match: string | null;
  /** OR7000 for a request (once numbered), else the short id. */
  reference?: string;
}

/** Hub tab: website work by routing — what needs a rep, and what is mine. */
export function RoutingQueue({ onOpen }: { onOpen: (kind: FlowKind, id: string) => void }) {
  const settings = useFlowSettings();
  const [scope, setScope] = useState<"needs_rep" | "mine" | "assigned">("needs_rep");
  const [rows, setRows] = useState<QueueRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!settings?.on) return;
    let live = true;
    setRows(null); setError(null);
    api<{ items: QueueRow[] }>(`/website-order-flow/queue?scope=${scope}`)
      .then((r) => { if (live) setRows(r.items); })
      .catch((e) => { if (live) setError(apiErrorMessage(e, "Couldn't load.")); });
    return () => { live = false; };
  }, [scope, settings?.on]);
  if (!settings) return null;
  if (!settings.on) return <p className="mt-6 text-[16px] text-muted">Account routing is switched off.</p>;
  const money = (v: string | null) => (v === null ? "Request" : Number(v).toLocaleString("en-US", { style: "currency", currency: "USD" }));
  return (
    <div className="mt-6">
      <div className="flex gap-5" role="group" aria-label="Which work">
        {([["needs_rep", "Needs a rep"], ["mine", "Mine"], ["assigned", "All assigned"]] as const).map(([k, label]) => (
          <button key={k} type="button" aria-pressed={scope === k} onClick={() => setScope(k)}
            className={cn("border-b pb-1 text-[13px] font-semibold", focusRing, scope === k ? "border-ink text-ink" : "border-transparent text-muted hover:text-ink")}>{label}</button>
        ))}
      </div>
      {error ? <p role="alert" className="mt-4 text-risk">{error}</p> : null}
      {rows === null && !error ? <p className="mt-6 flex items-center gap-2 text-muted"><Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading…</p> : null}
      {rows && rows.length === 0 ? <p className="mt-6 text-muted">{scope === "needs_rep" ? "Nothing waiting for a rep." : "Nothing here."}</p> : null}
      {rows && rows.length > 0 ? (
        <div className="mt-4 overflow-x-auto rounded-lg border border-rule">
          <table className="w-full min-w-[720px] text-left text-[13px] tabular-nums">
            <caption className="sr-only">Website orders and requests by rep</caption>
            <thead className="bg-canvas text-muted">
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold">Account</th>
                <th scope="col" className="px-4 py-3 font-semibold">What</th>
                <th scope="col" className="px-4 py-3 font-semibold">Rep</th>
                <th scope="col" className="px-4 py-3 font-semibold">When</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.kind}-${r.id}`} className="border-t border-rule">
                  <th scope="row" className="px-4 py-3 text-left font-normal">
                    <button type="button" className={cn("rounded text-left font-semibold underline-offset-2 hover:underline", focusRing)} onClick={() => onOpen(r.kind, r.id)}>
                      {r.client_name ?? r.contact.company ?? r.contact.name}
                    </button>
                    <div className="text-muted">{r.contact.name} · {r.contact.email}</div>
                  </th>
                  <td className="px-4 py-3">{r.kind === "order" ? `Order ${r.reference ?? r.id.slice(0, 8).toUpperCase()} · ${money(r.total)}` : `Request ${r.reference ?? r.id.slice(0, 8).toUpperCase()}`}</td>
                  <td className="px-4 py-3">{r.rep_name ?? <span className="rounded bg-warn-bg px-1.5 font-semibold text-warn">{r.routing_status === "needs_location" ? "Pick the location" : "Needs a rep"}</span>}</td>
                  <td className="px-4 py-3">{when(r.at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
